import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Rating,
  IconButton,
  Grid,
  Tooltip,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SparkAvatarBackground from 'sharedComponents/UI/SparkAvatarBackground';
import SparkdefaultIcon from 'components/SVG/SparkdefaultIcon';
import SparkOptionsModal from 'components/common/SparkOptionsModal';
import { BackIcon, MenuIcon } from 'components/SVG';
import { useDialog, useResize, useSafeNavigate } from 'hooks/common';
import {
  useAddSparkRating,
  useUpdateSpark,
  useUpdateSparkRating,
} from 'hooks/spark';
import {
  useCreateFavouriteList,
  useCreateSparkFavourite,
  useDeleteSparkFavourite,
} from 'hooks/Favourites';
import { useFlagReasons } from 'hooks/EntityTypes/useFlagReasons';
import { useAddUserFlag } from 'hooks/userFlags/useAddUserFlag';
import { getReviewText } from 'utilities/index';
import { getGropedSparkBadges } from 'helpers/groupSparkBadges';
import { SPARK_BADGE_TYPES } from 'constants/common';
import { useUserStore } from 'stores';
import { getCurrentDateISOString } from 'utilities/time';
import RunSparkModal from 'components/Sparks/Modals/RunSparkModal';
import SparkBadges from 'components/common/SparkBadges';
import { CreateFavouriteListFormValues } from 'components/common/Favourite/CreateFavouriteListPopUp';
import { SparkReportFormValues } from 'components/common/Report/SparkReport';
import { ReviewFormModalValues } from 'components/common/ReviewFormModal';
import { JoinedSparks } from 'types/Spark/JoinedSpark';

type SparkHeaderTypeProps = {
  joinedSpark: JoinedSparks | undefined;
  toggleOpen?: () => void;
};

const SparkRunsHeader = React.forwardRef(function SparkRunsHeader(
  { joinedSpark: spark, toggleOpen }: SparkHeaderTypeProps,
  ref: React.ForwardedRef<HTMLDivElement | null>
) {
  const currentUserID = useUserStore(
    React.useCallback((state) => state.currentUserID, [])
  );
  const { handleRouteBack, navigate } = useSafeNavigate();
  const [searchParams] = useSearchParams();
  const callbackURL = searchParams.get('callbackURL');
  const infoParam = searchParams.get('info');

  const { mutateAsync: createFavoriteList } = useCreateFavouriteList();
  const { mutateAsync: updateSpark } = useUpdateSpark(spark?.id);
  const {
    mutateAsync: createSparkFavorites,
    isLoading: isCreatingSparkFavourite,
  } = useCreateSparkFavourite();
  const {
    mutateAsync: deleteSparkFavorites,
    isLoading: isDeletingSparkFavourite,
  } = useDeleteSparkFavourite();
  const { mutateAsync: addUserFlag } = useAddUserFlag();

  const { dropdownOptions: flagReasonsDropdownOptions } = useFlagReasons();
  const existingFavouriteList = spark?.meta.user.favouriteLists;

  const {
    openDialog: openSparkRunDialog,
    closeDialog: closeSparkRunDialog,
    open: isSparkRunDialogOpen,
  } = useDialog();

  useEffect(() => {
    if (infoParam === '1') {
      openSparkRunDialog();
    }
  }, [infoParam, openSparkRunDialog]);

  const handleCreateList = async (values: CreateFavouriteListFormValues) => {
    const res = await createFavoriteList(values.listName);

    const sparkFavourite = {
      sparkID: spark?.id,
      userID: currentUserID,
      favouriteListID: Number(res.data.id),
    };
    await createSparkFavorites(sparkFavourite);
  };

  const handleFavouriteListItem = async (listID: number) => {
    if (existingFavouriteList?.some((listItem) => listItem.id === listID)) {
      const sparkFavourite = spark?.sparkFavourites?.find(
        (favourite) => favourite.attributes.favouriteListID === listID
      );
      await deleteSparkFavorites(sparkFavourite?.id || '');
      return;
    }
    const sparkFavourite = {
      sparkID: spark?.id,
      userID: currentUserID,
      favouriteListID: listID,
    };
    await createSparkFavorites(sparkFavourite);
  };

  const isLoadingSparkFavourites =
    isCreatingSparkFavourite || isDeletingSparkFavourite;

  const { isMobile } = useResize();
  const { ratings, permissions } = spark?.meta.user || {};
  const allowedToReview = permissions?.canReview;
  const userRating = ratings?.find((rating) => rating.sparkID === spark?.id);

  const { mutateAsync: addSparkRating } = useAddSparkRating();
  const { mutateAsync: updateSparkRating } = useUpdateSparkRating(
    userRating?.id ? `${userRating?.id}` : undefined
  );

  const handleSparkRating = async (values: ReviewFormModalValues) => {
    const doesReviewExists = !!userRating;
    if (!doesReviewExists) {
      await addSparkRating({ ...values, sparkID: spark?.id });
    }
    if (doesReviewExists) {
      await updateSparkRating({ ...values, sparkID: spark?.id });
    }
  };

  const handleSendReport = async (values: SparkReportFormValues) => {
    const userFlag = {
      flagReasonID: +values.reasonID,
      sparkID: spark?.id,
      comments: values.comments,
    };
    await addUserFlag(userFlag);
  };

  const handleBackClick = () => {
    if (callbackURL) {
      navigate(callbackURL, { state: { selectedTabIndex: 1 } });
      return;
    }
    handleRouteBack();
  };

  const badgesCategoryMap = getGropedSparkBadges(spark?.meta.badges);

  const handleDeleteSpark = async () => {
    await updateSpark({
      deletedAt: getCurrentDateISOString(),
      isDeleted: true,
    });
  };

  const handleArchiveSpark = async () => {
    await updateSpark({
      isArchived: true,
    });
  };

  const userPermissions = spark?.meta.user.permissions;

  return (
    <Grid
      ref={ref}
      container
      alignItems={'center'}
      pl={currentUserID ? 2 : 0}
      pr={2}
      justifyContent='space-between'
    >
      <Box display='flex' justifyContent='center' alignItems='center' gap={1}>
        {isMobile && toggleOpen && (
          <Box>
            <IconButton
              onClick={() => {
                toggleOpen && toggleOpen();
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        )}
        {!isMobile && currentUserID && (
          <Button
            onClick={handleBackClick}
            sx={{
              minWidth: 'unset',
              width: '45px',
              height: '45px',
              padding: 0,
              borderWidth: '1px',
              borderRadius: '14px',
              borderColor: '#EEEEEE',
              aspectRatio: 1,
            }}
            variant='outlined'
          >
            <BackIcon />
          </Button>
        )}
        <Box sx={{ display: 'flex', ml: 2 }}>
          <SparkAvatarBackground
            src={spark?.attributes?.iconURL || ''}
            rounded={false}
            icon={<SparkdefaultIcon width={'72px'} height={'72px'} />}
            width='72px'
            height='72px'
          />
        </Box>
        <Box paddingLeft={1.5} display='flex' flexDirection='column'>
          <Box display='flex' alignItems='flex-start' gap={1}>
            <Typography
              variant='body1'
              color={(theme) => theme.palette.common.black}
            >
              {spark?.attributes.nameforHuman}
            </Typography>
            <Tooltip
              title={'Click to learn more'}
              placement='top'
              arrow
              enterTouchDelay={0}
              onClick={openSparkRunDialog}
              style={{ cursor: 'pointer' }}
            >
              <InfoOutlinedIcon
                fontSize='small'
                sx={{ color: 'var(--color-label)' }}
              />
            </Tooltip>
          </Box>
          <Box display='flex' alignItems='center' gap={1}>
            <Box>
              <SparkBadges
                badges={badgesCategoryMap?.[SPARK_BADGE_TYPES.Creator] || []}
              />
            </Box>
            <Rating
              readOnly
              value={spark?.meta.averageRating ?? 1}
              precision={0.1}
              max={1}
            />
            <Typography variant='body2' color='black' paddingTop={0.5}>
              {getReviewText(spark?.meta.numberOfRatings)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid
        item
        container
        flex={1}
        gap={1}
        justifyContent='flex-end'
        flexWrap='nowrap'
      >
        <SparkOptionsModal
          resourceLogoURL={spark?.attributes.iconURL || ''}
          privacyTypeId={spark?.attributes.sparkPrivacyTypeID}
          resourceAccessLinkToken={spark?.attributes.accessLinkToken}
          options={spark?.meta.options}
          handleSparkRatingSave={handleSparkRating}
          userRating={userRating}
          allowedToReview={allowedToReview}
          resourceType='spark'
          isLoading={isLoadingSparkFavourites}
          onFavouriteItemClick={handleFavouriteListItem}
          onCreateList={handleCreateList}
          existingFavouriteList={existingFavouriteList || []}
          resourceID={spark?.id}
          onSendReport={handleSendReport}
          isResourceFavourite={spark?.meta.user.isFavourited}
          flagReasonsDropdownOptions={flagReasonsDropdownOptions}
          onDelete={handleDeleteSpark}
          onArchive={handleArchiveSpark}
          resourceName={spark?.attributes?.nameforHuman || '- -'}
          showHeartIcon={!!currentUserID}
          showDotsIcon={!!currentUserID}
          userPermissions={userPermissions}
        />
      </Grid>
      {isSparkRunDialogOpen && (
        <RunSparkModal
          isSparkRunDialogOpen={isSparkRunDialogOpen}
          closeSparkRunDialog={closeSparkRunDialog}
          sparkID={spark?.id}
        />
      )}
    </Grid>
  );
});

export default SparkRunsHeader;
